import type {CourseAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {CourseAssignmentStateEnum} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import {formatCompactAgo} from 'src/shared/utils/formatters';

/**
 * The sole copy, shared by every Training page that renders a course
 * assignment status pill.
 */
export const CourseAssignmentPillConfig = (
  status?: CourseAssignmentStateEnum,
  active?: boolean,
  employeeActive?: boolean,
  dueAt?: Date,
  expirationAt?: Date,
  completedAt?: Date,
  assignedAt?: Date,
  // Drop the standalone status pill when the dated pill it duplicates
  // (`Complete: 07/08/2026`, `Due: ...`) is present. Edit/view pages that
  // don't always populate the dated fields should leave this false.
  dedupeStatusText = false
): PillProps[] => {
  const pills: PillProps[] = [];

  switch (status) {
    case CourseAssignmentStateEnum.Pending:
      pills.push({text: 'In-progress', color: 'blue'});
      break;
    case CourseAssignmentStateEnum.Complete:
      if (!dedupeStatusText || !completedAt) {
        pills.push({text: 'Complete', color: 'green'});
      }
      break;
    case CourseAssignmentStateEnum.Locked:
      pills.push({text: 'Locked', color: 'red'});
      break;
    case CourseAssignmentStateEnum.Open:
      if (!dedupeStatusText || !dueAt) {
        pills.push({text: 'Open', color: 'yellow'});
      }
      break;
    case undefined:
    case CourseAssignmentStateEnum.Invalid:
    default:
      break;
  }

  if (active === true) {
    pills.push({text: 'Active Assignment', color: 'green'});
  } else if (active === false) {
    pills.push({text: 'Inactive Assignment', color: 'yellow'});
  }

  if (employeeActive === true) {
    pills.push({text: 'Active User', color: 'green'});
  } else if (employeeActive === false) {
    pills.push({text: 'Inactive User', color: 'yellow'});
  }

  if (assignedAt) {
    pills.push({
      text: `Assigned: ${assignedAt.toString().toShortDate()}`,
      color: 'blue',
    });
  }

  if (dueAt && status !== CourseAssignmentStateEnum.Complete) {
    if (new Date(dueAt) < new Date()) {
      pills.push({
        text: `Past Due: ${dueAt.toString().toShortDate()}`,
        color: 'red',
      });
    } else {
      pills.push({
        text: `Due: ${dueAt.toString().toShortDate()}`,
        color: 'gray',
      });
    }
  }

  if (completedAt) {
    pills.push({
      text: `Complete: ${completedAt.toString().toShortDate()}`,
      color: 'green',
    });
  }

  if (expirationAt && status === CourseAssignmentStateEnum.Complete) {
    pills.push({
      text: `Renew: ${expirationAt.toString().toShortDate()}`,
      color: 'gray',
    });
  }

  return pills;
};

/** Marks a course with no contents, which a trainer completes through a classroom. */
export const ClassroomOnlyPill: PillProps = {text: 'Classroom', color: 'gray'};

/** Pills for a course assignment list row (state, due/complete/renew dates). */
export const CourseAssignmentRowPills = (
  item: CourseAssignmentSimpleResponse
): PillProps[] => [
  ...CourseAssignmentPillConfig(
    item.state,
    undefined,
    undefined,
    item.dueAt,
    item.expectedExpirationAt,
    item.completedAt,
    undefined,
    true
  ),
  ...(item.isClassroomOnly ? [ClassroomOnlyPill] : []),
];

/** List-row subheader: course identifier, then assignment age. */
export const courseAssignmentRowSubText = (
  item: CourseAssignmentSimpleResponse
): string => {
  const assigned = formatCompactAgo(item.assignedAt);
  return [
    item.courseDefinition?.internalIdentifier,
    assigned && `Assigned ${assigned}`,
  ]
    .filter(Boolean)
    .join(' - ');
};
